import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { updateSafeAreaInsets } from '../state/safeArea';

type ViewportSize = {
  width: number;
  height: number;
};

const defaultViewport: ViewportSize = { width: 0, height: 0 };

const ViewportContext = createContext<ViewportSize>(defaultViewport);

const readViewportSize = (): ViewportSize => {
  if (typeof window === 'undefined') {
    return defaultViewport;
  }

  return {
    width: window.innerWidth,
    height: Math.min(
      window.visualViewport?.height ?? window.innerHeight,
      window.innerHeight
    ),
  };
};

const readSafeAreaInsets = () => {
  if (typeof window === 'undefined') {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }

  const root = document.documentElement;
  const style = window.getComputedStyle(root);
  const read = (name: string) => {
    const value = Number.parseFloat(style.getPropertyValue(name));
    return Number.isFinite(value) ? value : 0;
  };

  return {
    top: read('--safe-top'),
    right: read('--safe-right'),
    bottom: read('--safe-bottom'),
    left: read('--safe-left'),
  };
};

type ProviderProps = {
  children: ReactNode;
};

export const ViewportProvider = ({ children }: ProviderProps) => {
  const [viewport, setViewport] = useState<ViewportSize>(() => readViewportSize());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      setViewport(readViewportSize());
      updateSafeAreaInsets(readSafeAreaInsets());
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('resize', handleResize);
    window.visualViewport?.addEventListener('scroll', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.visualViewport?.removeEventListener('scroll', handleResize);
    };
  }, []);

  const value = useMemo(() => viewport, [viewport]);

  return <ViewportContext.Provider value={value}>{children}</ViewportContext.Provider>;
};

export const useViewportSize = () => {
  const context = useContext(ViewportContext);
  return context;
};
